"use strict";
/**
 * Copyright 2017 Google Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var CacheNode_1 = require("./view/CacheNode");
var ChildrenNode_1 = require("./snap/ChildrenNode");
var util_1 = require("@firebase/util");
var util_2 = require("@firebase/util");
var ViewCache_1 = require("./view/ViewCache");
var View_1 = require("./view/View");
var __referenceConstructor;
/**
 * SyncPoint represents a single location in a SyncTree with 1 or more event registrations, meaning we need to
 * maintain 1 or more Views at this location to cache server data and raise appropriate events for server changes
 * and user writes (set, transaction, update).
 *
 * It's responsible for:
 * - Maintaining the set of 1 or more views necessary at this location (a SyncPoint with 0 views should be removed).
 * - Proxying user / server operations to the views as appropriate (i.e. applyServerOverwrite,
 * applyUserOverwrite, etc.)
 */
var SyncPoint = /** @class */ (function () {
 function SyncPoint() {
 /**
 * The Views being tracked at this location in the tree, stored as a map where the key is a
 * queryId and the value is the View for that query.
 *
 * NOTE: This list will be quite small (usually 1, but perhaps 2 or 3; any more is an odd use case).
 *
 * @type {!Object.}
 * @private
 */
 this.views_ = {};
 }
 Object.defineProperty(SyncPoint, "__referenceConstructor", {
 get: function () {
 util_1.assert(__referenceConstructor, 'Reference.ts has not been loaded');
 return __referenceConstructor;
 },
 set: function (val) {
 util_1.assert(!__referenceConstructor, '__referenceConstructor has already been defined');
 __referenceConstructor = val;
 },
 enumerable: true,
 configurable: true
 });
 /**
 * @return {boolean}
 */
 SyncPoint.prototype.isEmpty = function () {
 return util_2.isEmpty(this.views_);
 };
 /**
 *
 * @param {!Operation} operation
 * @param {!WriteTreeRef} writesCache
 * @param {?Node} optCompleteServerCache
 * @return {!Array.}
 */
 SyncPoint.prototype.applyOperation = function (operation, writesCache, optCompleteServerCache) {
 var queryId = operation.source.queryId;
 if (queryId !== null) {
 var view = util_2.safeGet(this.views_, queryId);
 util_1.assert(view != null, 'SyncTree gave us an op for an invalid query.');
 return view.applyOperation(operation, writesCache, optCompleteServerCache);
 }
 else {
 var events_1 = [];
 util_2.forEach(this.views_, function (key, view) {
 events_1 = events_1.concat(view.applyOperation(operation, writesCache, optCompleteServerCache));
 });
 return events_1;
 }
 };
 /**
 * Add an event callback for the specified query.
 *
 * @param {!Query} query
 * @param {!EventRegistration} eventRegistration
 * @param {!WriteTreeRef} writesCache
 * @param {?Node} serverCache Complete server cache, if we have it.
 * @param {boolean} serverCacheComplete
 * @return {!Array.} Events to raise.
 */
 SyncPoint.prototype.addEventRegistration = function (query, eventRegistration, writesCache, serverCache, serverCacheComplete) {
 var queryId = query.queryIdentifier();
 var view = util_2.safeGet(this.views_, queryId);
 if (!view) {
 // TODO: make writesCache take flag for complete server node
 var eventCache = writesCache.calcCompleteEventCache(serverCacheComplete ? serverCache : null);
 var eventCacheComplete = false;
 if (eventCache) {
 eventCacheComplete = true;
 }
 else if (serverCache instanceof ChildrenNode_1.ChildrenNode) {
 eventCache = writesCache.calcCompleteEventChildren(serverCache);
 eventCacheComplete = false;
 }
 else {
 eventCache = ChildrenNode_1.ChildrenNode.EMPTY_NODE;
 eventCacheComplete = false;
 }
 var viewCache = new ViewCache_1.ViewCache(new CacheNode_1.CacheNode(
 /** @type {!Node} */ (eventCache), eventCacheComplete, false), new CacheNode_1.CacheNode(
 /** @type {!Node} */ (serverCache), serverCacheComplete, false));
 view = new View_1.View(query, viewCache);
 this.views_[queryId] = view;
 }
 // This is guaranteed to exist now, we just created anything that was missing
 view.addEventRegistration(eventRegistration);
 return view.getInitialEvents(eventRegistration);
 };
 /**
 * Remove event callback(s). Return cancelEvents if a cancelError is specified.
 *
 * If query is the default query, we'll check all views for the specified eventRegistration.
 * If eventRegistration is null, we'll remove all callbacks for the specified view(s).
 *
 * @param {!Query} query
 * @param {?EventRegistration} eventRegistration If null, remove all callbacks.
 * @param {Error=} cancelError If a cancelError is provided, appropriate cancel events will be returned.
 * @return {{removed:!Array., events:!Array.}} removed queries and any cancel events
 */
 SyncPoint.prototype.removeEventRegistration = function (query, eventRegistration, cancelError) {
 var queryId = query.queryIdentifier();
 var removed = [];
 var cancelEvents = [];
 var hadCompleteView = this.hasCompleteView();
 if (queryId === 'default') {
 // When you do ref.off(...), we search all views for the registration to remove.
 var self_1 = this;
 util_2.forEach(this.views_, function (viewQueryId, view) {
 cancelEvents = cancelEvents.concat(view.removeEventRegistration(eventRegistration, cancelError));
 if (view.isEmpty()) {
 delete self_1.views_[viewQueryId];
 // We'll deal with complete views later.
 if (!view
 .getQuery()
 .getQueryParams()
 .loadsAllData()) {
 removed.push(view.getQuery());
 }
 }
 });
 }
 else {
 // remove the callback from the specific view.
 var view = util_2.safeGet(this.views_, queryId);
 if (view) {
 cancelEvents = cancelEvents.concat(view.removeEventRegistration(eventRegistration, cancelError));
 if (view.isEmpty()) {
 delete this.views_[queryId];
 // We'll deal with complete views later.
 if (!view
 .getQuery()
 .getQueryParams()
 .loadsAllData()) {
 removed.push(view.getQuery());
 }
 }
 }
 }
 if (hadCompleteView && !this.hasCompleteView()) {
 // We removed our last complete view.
 removed.push(new SyncPoint.__referenceConstructor(query.repo, query.path));
 }
 return { removed: removed, events: cancelEvents };
 };
 /**
 * @return {!Array.}
 */
 SyncPoint.prototype.getQueryViews = function () {
 var _this = this;
 var values = Object.keys(this.views_).map(function (key) { return _this.views_[key]; });
 return values.filter(function (view) {
 return !view
 .getQuery()
 .getQueryParams()
 .loadsAllData();
 });
 };
 /**
 *
 * @param {!Path} path The path to the desired complete snapshot
 * @return {?Node} A complete cache, if it exists
 */
 SyncPoint.prototype.getCompleteServerCache = function (path) {
 var serverCache = null;
 util_2.forEach(this.views_, function (key, view) {
 serverCache = serverCache || view.getCompleteServerCache(path);
 });
 return serverCache;
 };
 /**
 * @param {!Query} query
 * @return {?View}
 */
 SyncPoint.prototype.viewForQuery = function (query) {
 var params = query.getQueryParams();
 if (params.loadsAllData()) {
 return this.getCompleteView();
 }
 else {
 var queryId = query.queryIdentifier();
 return util_2.safeGet(this.views_, queryId);
 }
 };
 /**
 * @param {!Query} query
 * @return {boolean}
 */
 SyncPoint.prototype.viewExistsForQuery = function (query) {
 return this.viewForQuery(query) != null;
 };
 /**
 * @return {boolean}
 */
 SyncPoint.prototype.hasCompleteView = function () {
 return this.getCompleteView() != null;
 };
 /**
 * @return {?View}
 */
 SyncPoint.prototype.getCompleteView = function () {
 var completeView = util_2.findValue(this.views_, function (view) {
 return view
 .getQuery()
 .getQueryParams()
 .loadsAllData();
 });
 return completeView || null;
 };
 return SyncPoint;
}());
exports.SyncPoint = SyncPoint;

//# sourceMappingURL=SyncPoint.js.map



//////////////////
// WEBPACK FOOTER
// ../database/dist/cjs/src/core/SyncPoint.js
// module id = 47
// module chunks = 0